import { DoubleLeftOutlined, DoubleRightOutlined } from "@ant-design/icons";
import { Collapse } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CopyableText } from "../../copyable-text";
import { ContentType } from "../../../api";

const { Panel } = Collapse;

interface VariableList {
  group: {
    en: { name: string; description: string };
    de: { name: string; description: string };
  };
  variables: {
    name: string;
    nameAlt: string;
    example: string;
    en: { type: string; description: string };
    de: { type: string; description: string };
  }[];
}

interface VariableDefintionsDrawerProps {
  variablesDrawerCollapsed: boolean;
  selectedFormat: ContentType;
  setVariablesDrawerCollapsed: (collapsed: boolean) => void;
}

export function VariableDefintionsDrawer({
  variablesDrawerCollapsed,
  selectedFormat,
  setVariablesDrawerCollapsed,
}: VariableDefintionsDrawerProps) {
  const { t, i18n } = useTranslation();
  const [templateVariables, setTemplateVariables] = useState<any>([]);

  useEffect(() => {
    fetch("/template-variables.json")
      .then((response) => response.json())
      .then((data) => setTemplateVariables(data))
      .catch((error) =>
        console.error("Error loading template variables:", error)
      );
  }, []);

  return (
    <div
      className={
        variablesDrawerCollapsed
          ? "panel-info-collapsed"
          : "panel-info-expanded"
      }
    >
      {variablesDrawerCollapsed ? (
        <div
          onClick={() => setVariablesDrawerCollapsed(!variablesDrawerCollapsed)}
          style={{
            cursor: "pointer",
          }}
        >
          <DoubleLeftOutlined />
          <div
            style={{
              writingMode: "vertical-rl",
              textOrientation: "mixed",
              marginTop: "0.5rem",
              fontWeight: "bold",
            }}
          >
            {t("integrations:variable-definitions-title")}
          </div>
        </div>
      ) : (
        <div>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() =>
              setVariablesDrawerCollapsed(!variablesDrawerCollapsed)
            }
          >
            <DoubleRightOutlined />
            <span
              style={{
                marginLeft: "0.5rem",
                fontWeight: "bold",
              }}
            >
              {t("integrations:variable-definitions-title")}
            </span>
          </span>
          <div style={{ marginTop: "1rem" }}>
            <Collapse defaultActiveKey={["1"]}>
              {templateVariables.map((group: VariableList) => (
                <Panel
                  header={
                    i18n.language === "en"
                      ? group.group.en.name
                      : group.group.de.name
                  }
                  key={group.group.en.name}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th
                          style={{
                            border: "1px solid #d9d9d9",
                            padding: "8px",
                            textAlign: "left",
                          }}
                        >
                          {t("variable")}
                        </th>
                        <th
                          style={{
                            border: "1px solid #d9d9d9",
                            padding: "8px",
                            textAlign: "left",
                          }}
                        >
                          {t("type")}
                        </th>
                        <th
                          style={{
                            border: "1px solid #d9d9d9",
                            padding: "8px",
                            textAlign: "left",
                          }}
                        >
                          {t("description")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.variables.map((variable: any) => (
                        <tr key={variable.name}>
                          <td
                            style={{
                              border: "1px solid #d9d9d9",
                              padding: "8px",
                            }}
                          >
                            {selectedFormat === ContentType.JSON ? (
                              <CopyableText text={`"${variable.name}"`} />
                            ) : (
                              <CopyableText text={variable.name} />
                            )}
                          </td>
                          <td
                            style={{
                              border: "1px solid #d9d9d9",
                              padding: "8px",
                            }}
                          >
                            {i18n.language === "en"
                              ? variable.en.type
                              : variable.de.type}
                          </td>
                          <td
                            style={{
                              border: "1px solid #d9d9d9",
                              padding: "8px",
                              whiteSpace: "pre-line",
                            }}
                          >
                            {i18n.language === "en"
                              ? variable.en.description
                              : variable.de.description}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Panel>
              ))}
            </Collapse>
          </div>
        </div>
      )}
    </div>
  );
}
